import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Users,
    Instagram,
    Facebook,
    Linkedin,
    Twitter,
    Info,
    CheckCircle,
    Loader,
    Trash2,
    Paintbrush,
    Eye,
    ChevronUp,
    Phone,
    Globe,
    X,
    AlertTriangle,
    Plus,
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { GlassTabs } from '../ui/GlassTabs';
import { GlassBadge } from '../ui/GlassBadge';
import { ModuleHeader } from '../ui/ModuleHeader';
import {
    getSocialAccounts,
    createSocialAccount,
    deleteSocialAccount,
    applySocialBranding,
    applyAllSocialBranding,
    ApplyBrandingAllResult,
    getSocialBrandingPreview,
    createTwoFASession,
    getTwoFASession,
    completeTwoFASession,
    checkFacebookOAuthConfig,
    getFacebookPages,
    connectFacebookPages,
    resetFacebookCredential,
    checkLinkedInOAuthConfig,
    connectLinkedInAccount,
    getLinkedInPages,
    connectLinkedInPages,
    checkInstagramOAuthConfig,
    getInstagramAccounts,
    connectInstagramAccounts,
    checkTwitterOAuthConfig,
    connectTwitterAccount,
    createBrandingDraft,
    getBrandingDraft,
    BrandingDraftResponse,
} from '../../services/api';
import { FacebookBrandingModal } from './FacebookBrandingModal';
import { groupAccountsByPlatform, accountIdentityLabel, SocialAccountData, PlatformKey } from './groupAccounts';
import './SocialAccounts.css';

// FLB feature flag: read env var OR localStorage backdoor (for admin testing
// during App Review wait without requiring a rebuild).
// URL toggle for Meta reviewers (no DevTools needed): a full page load of
// /social-accounts?flb=1 persists the backdoor for this browser; ?flb=0 clears it.
if (typeof window !== 'undefined') {
  const flbParam = new URLSearchParams(window.location.search).get('flb');
  if (flbParam === '1') window.localStorage?.setItem('aime_facebook_flb', '1');
  else if (flbParam === '0') window.localStorage?.removeItem('aime_facebook_flb');
}
// Evaluated at call time (render + click), NOT captured in a module constant:
// a stale constant meant ?flb=1 set after bundle evaluation silently fell back
// to the standard login — the connect looked like FLB but sent use_flb=false.
// Also honours the live URL param directly so even SPA navigation works.
const isFlbEnabled = (): boolean =>
  process.env.REACT_APP_FACEBOOK_FLB_ENABLED === 'true' ||
  (typeof window !== 'undefined' && (
    window.localStorage?.getItem('aime_facebook_flb') === '1' ||
    new URLSearchParams(window.location.search).get('flb') === '1'
  ));

// "Add another <platform> account" shortcut card on the Connected Accounts tab.
// HIDDEN BY DEFAULT (product decision 2026-09-30, specs/completed/flb-default-hide-add-account.md).
// Hidden, NOT removed: every account still gets its own card, per-account
// Disconnect/Re-apply is unchanged, the Facebook page picker still connects
// several Pages in one go, and the Setup Wizard still connects additional
// accounts. Set to true to restore the card exactly as it rendered before
// (the markup, label helper and .platform-card-add CSS are all kept for this).
// If you flip this, also re-read assistant_knowledge.py's social_accounts
// entry (its text is written to be correct either way; see that spec).
const SHOW_ADD_ANOTHER_ACCOUNT_CARD: boolean = false;

/**
 * Opens the same FLB-aware Facebook OAuth popup as WizardPlatformCard's
 * "Connect with Facebook" button (see handleOAuthPopup('facebook') below) —
 * extracted here so the branding Reconnect flow (BrandingCapabilityBanner /
 * FacebookBrandingModal, Phase 2 of facebook-page-branding-live-push) can
 * trigger it without WizardPlatformCard needing to be the caller. Must be
 * called synchronously from a click handler (no awaits before it) or the
 * browser's popup blocker will swallow it.
 */
const openFacebookOAuthPopup = (projectId: string | undefined | null) => {
  if (!projectId) {
    alert('Select a project first');
    return;
  }
  const token = localStorage.getItem('access_token') || '';
  const width = 600, height = 700;
  const left = Math.floor((window.innerWidth - width) / 2);
  const top = Math.floor((window.innerHeight - height) / 2);
  const useFlb = isFlbEnabled();
  const oauthUrl = `/api/oauth/facebook/authorize?project_id=${projectId}&token=${encodeURIComponent(token)}${useFlb ? '&use_flb=true' : ''}`;
  window.open(
    oauthUrl,
    'oauth_popup',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
  );
};

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

/**
 * Five-state badge switch driven by branding_status, replacing the old
 * binary branding_applied boolean. See
 * specs/completed/facebook-page-branding-live-push.md "Render site audit —
 * branding_applied / branding_applied_at / the accounts collection".
 * showNegative renders a "No Branding" badge for a NULL status (the
 * Branding tab does this deliberately; the Connected-Accounts tab does not,
 * to keep that row uncluttered, matching the spec's row-by-row table).
 */
const brandingStatusBadge = (
  status: string | null,
  showNegative: boolean,
): { variant: 'success' | 'warning' | 'info' | 'danger'; label: string } | null => {
  switch (status) {
    case 'applied':
      return { variant: 'success', label: 'Branding Applied' };
    case 'partial':
      return { variant: 'warning', label: 'Partially Applied' };
    case 'pending_approval':
      return { variant: 'info', label: 'Awaiting Approval' };
    case 'failed':
      return { variant: 'danger', label: 'Branding Failed' };
    default:
      return showNegative ? { variant: 'warning', label: 'No Branding' } : null;
  }
};


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
// SocialAccountData and PlatformKey now live in ./groupAccounts.ts, imported
// above, so the pure grouping helpers can be unit-tested independently of
// this component.

interface BrandingPreview {
    bio_text: string;
    profile_image_prompt: string;
    banner_image_prompt: string;
    stub_mode: boolean;
}

interface TwoFASessionData {
    session_id: string;
    phone_number: string;
    status: string;
    code: string | null;
    stub_mode: boolean;
}

const PLATFORMS: { key: PlatformKey; label: string; color: string }[] = [
    { key: 'instagram', label: 'Instagram', color: '#e1306c' },
    { key: 'facebook', label: 'Facebook', color: '#1877f2' },
    { key: 'linkedin', label: 'LinkedIn', color: '#0a66c2' },
    { key: 'twitter', label: 'Twitter / X', color: '#000000' },
];

const COUNTRY_OPTIONS = [
    { value: 'AU', label: 'Australia (+61)' },
    { value: 'NZ', label: 'New Zealand (+64)' },
    { value: 'GB', label: 'United Kingdom (+44)' },
    { value: 'US', label: 'United States (+1)' },
    { value: 'CA', label: 'Canada (+1)' },
    { value: 'IE', label: 'Ireland (+353)' },
];

const PLATFORM_SETUP_STEPS: Record<PlatformKey, string[]> = {
    instagram: [
        'Go to instagram.com and tap "Sign Up"',
        'Enter the AiMe-provided phone number',
        'Enter the 2FA code shown below',
        'Complete your profile setup',
    ],
    facebook: [
        'Go to facebook.com and click "Create New Account"',
        'Enter the AiMe-provided phone number',
        'Enter the confirmation code shown below',
        'Complete your profile and page setup',
    ],
    linkedin: [
        'Go to linkedin.com and click "Join Now"',
        'Enter the AiMe-provided phone number for verification',
        'Enter the verification code shown below',
        'Complete your professional profile',
    ],
    twitter: [
        'Go to twitter.com and click "Sign Up"',
        'Enter the AiMe-provided phone number',
        'Enter the confirmation code shown below',
        'Choose a username and complete setup',
    ],
};

// ---------------------------------------------------------------------------
// Platform Icon
// ---------------------------------------------------------------------------

const PlatformIcon: React.FC<{ platform: string; size?: number }> = ({ platform, size = 18 }) => {
    const p = platform.toLowerCase();
    if (p === 'instagram') return <Instagram size={size} />;
    if (p === 'facebook') return <Facebook size={size} />;
    if (p === 'linkedin') return <Linkedin size={size} />;
    if (p === 'twitter') return <Twitter size={size} />;
    return <Globe size={size} />;
};

const platformIconClass = (platform: string) => {
    const p = platform.toLowerCase();
    if (p === 'instagram') return 'platform-icon-instagram';
    if (p === 'facebook') return 'platform-icon-facebook';
    if (p === 'linkedin') return 'platform-icon-linkedin';
    if (p === 'twitter') return 'platform-icon-twitter';
    return 'platform-icon-linkedin';
};

// ---------------------------------------------------------------------------
// Per-Platform Wizard Card
// ---------------------------------------------------------------------------

interface FacebookPage {
    page_id: string;
    name: string;
    category: string;
    picture_url: string | null;
    already_connected: boolean;
    instagram: { instagram_id: string; username: string } | null;
    instagram_already_connected: boolean;
}

interface WizardPlatformCardProps {
    platform: typeof PLATFORMS[0];
    projectId?: string;
    onConnected: () => void;
    // Lifted to the SocialAccountsModule parent (see BLOCKER-1 fix,
    // flb-instagram-autocreate-scope-fix code review iteration 1): the
    // Facebook card is the one that discovers an auto-connected Instagram
    // account, but the wizard cards unmount the instant onConnected() switches
    // the tab to 'connected'. A per-card useState can never be seen by the
    // Instagram card (a different component instance) or survive the tab
    // switch, so the confirmation has to be reported up to a parent-owned
    // toast that renders outside the tab-gated wizard view.
    onIgAutoConnected?: (usernames: string) => void;
    oauthConfigured?: boolean;
    linkedinOAuthConfigured?: boolean;
    linkedinCompanyPagesEnabled?: boolean;
    instagramOAuthConfigured?: boolean;
    twitterOAuthConfigured?: boolean;
    existingAccounts?: SocialAccountData[];
}

const WizardPlatformCard: React.FC<WizardPlatformCardProps> = ({ platform, projectId, onConnected, onIgAutoConnected, oauthConfigured, linkedinOAuthConfigured, linkedinCompanyPagesEnabled, instagramOAuthConfigured, twitterOAuthConfigured, existingAccounts = [] }) => {
    const isOAuthAvailable = (platform.key === 'facebook' && oauthConfigured) || (platform.key === 'linkedin' && linkedinOAuthConfigured) || (platform.key === 'instagram' && instagramOAuthConfigured) || (platform.key === 'twitter' && twitterOAuthConfigured);
    const [accountMode, setAccountMode] = useState<'new' | 'oauth'>(
        isOAuthAvailable ? 'oauth' : 'new'
    );

    // "I need to create an account" state
    const [phoneChoice, setPhoneChoice] = useState<'aime' | 'own'>('aime');
    const [ownNumber, setOwnNumber] = useState('');
    const [countryCode, setCountryCode] = useState('AU');
    const [provisioning, setProvisioning] = useState(false);
    const [twoFASession, setTwoFASession] = useState<TwoFASessionData | null>(null);
    const [setupComplete, setSetupComplete] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [connecting, setConnecting] = useState(false);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // OAuth flow state (Facebook)
    const [oauthCredentialId, setOauthCredentialId] = useState<string | null>(null);
    const [facebookPages, setFacebookPages] = useState<FacebookPage[]>([]);
    const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());
    const [loadingPages, setLoadingPages] = useState(false);
    const [connectingPages, setConnectingPages] = useState(false);

    // LinkedIn OAuth state
    const [linkedinConnecting, setLinkedinConnecting] = useState(false);
    const [linkedinPages, setLinkedinPages] = useState<Array<{ id: string; urn: string; name: string; vanity_name: string }>>([]);
    const [linkedinCredentialId, setLinkedinCredentialId] = useState<string | null>(null);
    const [selectedLinkedinPageIds, setSelectedLinkedinPageIds] = useState<Set<string>>(new Set());
    const [loadingLinkedinPages, setLoadingLinkedinPages] = useState(false);
    const [connectingLinkedinPages, setConnectingLinkedinPages] = useState(false);

    // Twitter OAuth state
    const [twitterConnecting, setTwitterConnecting] = useState(false);

    // Instagram OAuth state
    const [instagramAccounts, setInstagramAccounts] = useState<Array<{ instagram_id: string; username: string; name: string; profile_picture_url: string; followers_count: number; page_name: string; already_connected: boolean }>>([]);
    const [selectedInstagramIds, setSelectedInstagramIds] = useState<Set<string>>(new Set());
    const [loadingInstagram, setLoadingInstagram] = useState(false);
    const [connectingInstagram, setConnectingInstagram] = useState(false);

    const toggleInstagramSelection = (igId: string) => {
        setSelectedInstagramIds(prev => {
            const next = new Set(prev);
            if (next.has(igId)) next.delete(igId); else next.add(igId);
            return next;
        });
    };

    const handleConnectInstagramAccounts = async () => {
        if (!projectId || !oauthCredentialId) return;
        setConnectingInstagram(true);
        try {
            await connectInstagramAccounts({
                project_id: projectId,
                credential_id: oauthCredentialId,
                page_ids: Array.from(selectedInstagramIds),
            });
            onConnected();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to connect Instagram accounts'));
        } finally {
            setConnectingInstagram(false);
        }
    };

    // Reusable Pages fetch — also wired to the "Reload Pages" button in the
    // empty-pages state: after an app removal/re-consent cycle Meta's
    // /me/accounts can lag a few minutes behind the (already granted) consent,
    // so retrying without redoing OAuth often resolves it.
    const reloadFacebookPages = async () => {
        if (!projectId) return;
        setLoadingPages(true);
        try {
            const result = await getFacebookPages(projectId);
            setFacebookPages(result.pages);
        } catch (err: any) {
            console.error('Failed to fetch Facebook pages:', err);
            alert(getApiError(err, 'Failed to fetch Facebook Pages'));
        } finally {
            setLoadingPages(false);
        }
    };

    // Listen for OAuth popup postMessage
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type !== 'oauth_success') return;

            if (event.data.platform === 'facebook' && event.data.needs_page_selection) {
                setOauthCredentialId(event.data.credential_id);
                await reloadFacebookPages();
            } else if (event.data.platform === 'instagram' && event.data.needs_page_selection) {
                setOauthCredentialId(event.data.credential_id);
                if (projectId) {
                    setLoadingInstagram(true);
                    try {
                        const result = await getInstagramAccounts(projectId);
                        setInstagramAccounts(result.accounts);
                    } catch (err: any) {
                        console.error('Failed to fetch Instagram accounts:', err);
                        alert(getApiError(err, 'Failed to fetch Instagram accounts'));
                    } finally {
                        setLoadingInstagram(false);
                    }
                }
            } else if (event.data.platform === 'linkedin' && event.data.needs_page_selection) {
                // Company Pages flow (LINKEDIN_COMPANY_PAGES_ENABLED=true)
                setLinkedinCredentialId(event.data.credential_id);
                if (projectId) {
                    setLoadingLinkedinPages(true);
                    try {
                        const result = await getLinkedInPages(projectId);
                        setLinkedinPages(result.pages);
                    } catch (err: any) {
                        alert(getApiError(err, 'Failed to fetch LinkedIn Company Pages'));
                    } finally {
                        setLoadingLinkedinPages(false);
                    }
                }
            } else if (event.data.platform === 'linkedin' && !event.data.needs_page_selection) {
                // Personal profile flow (default)
                if (projectId && event.data.credential_id) {
                    setLinkedinConnecting(true);
                    try {
                        await connectLinkedInAccount({
                            project_id: projectId,
                            credential_id: event.data.credential_id,
                        });
                        onConnected();
                    } catch (err: any) {
                        alert(getApiError(err, 'Failed to connect LinkedIn account'));
                    } finally {
                        setLinkedinConnecting(false);
                    }
                }
            } else if (event.data.platform === 'twitter' && !event.data.needs_page_selection) {
                if (projectId && event.data.credential_id) {
                    setTwitterConnecting(true);
                    try {
                        await connectTwitterAccount({
                            project_id: projectId,
                            credential_id: event.data.credential_id,
                        });
                        onConnected();
                    } catch (err: any) {
                        alert(getApiError(err, 'Failed to connect Twitter/X account'));
                    } finally {
                        setTwitterConnecting(false);
                    }
                }
            } else if (event.data.platform === 'instagram' && !event.data.needs_page_selection) {
                // IG-direct flow: backend normalises 'instagram_direct' → 'instagram' for the
                // db_platform / postMessage payload, so we MUST check 'instagram' here, not
                // 'instagram_direct'. needs_page_selection=false differentiates IG-direct
                // (SocialAccount created server-side) from FLB-linked-IG (which sets
                // needs_page_selection=true and is handled in the branch above).
                onConnected();
            }

            // NOTE: linked-Instagram auto-create used to be announced here via
            // event.data.auto_created_instagram, set by the FB-FLB OAuth
            // callback's eager auto-create (deleted — see
            // specs/completed/flb-instagram-autocreate-scope-fix.md). The
            // backend no longer emits this field. The "Auto-connected
            // Instagram" toast is now driven by handleConnectSelectedPages's
            // own response instead, since auto-create now happens at page-
            // picker-save time, not at OAuth-popup-close time.
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [projectId, onConnected]);

    const handleInstagramConnect = () => {
        // If an IG SocialAccount already exists, show confirmation before reconnecting.
        const existingIG = existingAccounts.find((a: SocialAccountData) =>
            a.platform === 'instagram' && a.status === 'active'
        );

        if (existingIG) {
            const isCurrentlyDirect = (existingIG as any).credential_is_ig_direct === true;

            if (isCurrentlyDirect) {
                alert(
                    `Instagram @${existingIG.username} is already connected directly. ` +
                    `If you want to reconnect, disconnect first.`
                );
                return;
            }

            // Currently connected via FLB-linked path
            const proceed = window.confirm(
                `Instagram @${existingIG.username} is currently connected via your Facebook Page. ` +
                `\n\nSwitching to direct Instagram Login is rarely needed — your existing connection ` +
                `works for publishing.\n\nContinue and switch to direct login?`
            );
            if (!proceed) return;
        }

        handleOAuthPopup('instagram_direct');
    };

    const handleOAuthPopup = (oauthPlatform: string) => {
        if (!projectId) {
            alert('Select a project first');
            return;
        }
        const token = localStorage.getItem('access_token') || '';
        const width = 600, height = 700;
        const left = Math.floor((window.innerWidth - width) / 2);
        const top = Math.floor((window.innerHeight - height) / 2);
        const useFlb = isFlbEnabled() && (oauthPlatform === 'facebook' || oauthPlatform === 'instagram');
        const oauthUrl = `/api/oauth/${oauthPlatform}/authorize?project_id=${projectId}&token=${encodeURIComponent(token)}${useFlb ? '&use_flb=true' : ''}`;
        window.open(
            oauthUrl,
            'oauth_popup',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
        );
    };

    const togglePageSelection = (pageId: string) => {
        setSelectedPageIds(prev => {
            const next = new Set(prev);
            if (next.has(pageId)) next.delete(pageId); else next.add(pageId);
            return next;
        });
    };

    const handleConnectSelectedPages = async () => {
        if (!projectId || !oauthCredentialId) return;
        setConnectingPages(true);
        try {
            const createdAccounts = await connectFacebookPages({
                project_id: projectId,
                credential_id: oauthCredentialId,
                page_ids: Array.from(selectedPageIds),
            });
            // Linked-Instagram auto-create now happens at save time, scoped to
            // the selected Page(s) — re-source the toast from THIS response
            // rather than the OAuth popup's postMessage (which no longer
            // carries it). Unlike the old callback path's "last one wins"
            // single object, this can be a list: one entry per selected Page
            // that had a linked Instagram account.
            //
            // Reported UP to the parent (onIgAutoConnected), not set on local
            // state: this card is the Facebook card, but onConnected() below
            // immediately switches the wizard's active tab to 'connected',
            // which unmounts every WizardPlatformCard instance (including
            // this one and the Instagram one) — a local toast here would
            // never be seen. Call it BEFORE onConnected() so the parent has
            // the message queued before the tab switch happens.
            const createdIg = (createdAccounts || []).filter((a: any) => a?.platform === 'instagram');
            if (createdIg.length > 0) {
                const usernames = createdIg.map((a: any) => a.username).filter(Boolean).join(', ');
                if (usernames) onIgAutoConnected?.(usernames);
            }
            onConnected();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to connect pages'));
        } finally {
            setConnectingPages(false);
        }
    };

    // Clears the Facebook ProjectCredential so the next "Log in with Facebook"
    // triggers a fresh OAuth flow. Used when Meta returns empty /me/accounts
    // and the user is stuck in the page-picker with no Pages to select.
    // This deletes the project's Facebook credential and nulls credential_id
    // on every connected Page referencing it (including other users' Pages
    // on a shared project) — see specs/completed/social-accounts-multi-page-fix.md
    // Open Question 1. Reserved for the explicit "Reset Facebook connection"
    // recovery action; the plain page-picker Cancel button below must NOT
    // call this (see handleCancelPageSelection).
    const handleResetFacebookCredential = async () => {
        if (!projectId) return;
        if (!window.confirm('Reset the Facebook connection for this project? You will need to log in with Facebook again. Pages already connected here stay connected and keep publishing, but will show as needing a reconnect in some publish pickers until you reconnect.')) return;
        try {
            await resetFacebookCredential(projectId);
            setOauthCredentialId(null);
            setFacebookPages([]);
            setSelectedPageIds(new Set());
        } catch (err: any) {
            alert(getApiError(err, 'Failed to reset Facebook connection'));
        }
    };

    // Cancelling the page picker should only abandon the in-progress
    // selection, not touch the backend credential — unlike the explicit
    // "Reset Facebook connection" button above. Previously both called
    // handleResetFacebookCredential, so simply backing out of the picker
    // silently detached already-connected Pages (including other users'
    // Pages on a shared project) from their OAuth credential.
    const handleCancelPageSelection = () => {
        setOauthCredentialId(null);
        setFacebookPages([]);
        setSelectedPageIds(new Set());
    };

    const handleConnectLinkedinPages = async () => {
        if (!projectId || !linkedinCredentialId) return;
        setConnectingLinkedinPages(true);
        try {
            const selected = linkedinPages.filter(p => selectedLinkedinPageIds.has(p.id));
            await connectLinkedInPages({
                project_id: projectId,
                credential_id: linkedinCredentialId,
                page_ids: selected,
            });
            onConnected();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to connect LinkedIn Company Pages'));
        } finally {
            setConnectingLinkedinPages(false);
        }
    };

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);

    const startPolling = useCallback((sessionId: string) => {
        stopPolling();
        pollingRef.current = setInterval(async () => {
            try {
                const data = await getTwoFASession(sessionId);
                if (data.status === 'received' || data.status === 'expired') {
                    stopPolling();
                    setTwoFASession((prev) => prev ? { ...prev, ...data } : prev);
                }
            } catch {
                stopPolling();
            }
        }, 3000);
    }, [stopPolling]);

    const handleProvisionNumber = async () => {
        setProvisioning(true);
        try {
            const data = await createTwoFASession({
                platform: platform.key,
                country_code: countryCode,
                use_own_number: phoneChoice === 'own',
                own_number: phoneChoice === 'own' ? ownNumber : undefined,
            });
            setTwoFASession(data);
            if (phoneChoice === 'aime') {
                startPolling(data.session_id);
            }
        } catch (err: any) {
            alert(getApiError(err, 'Failed to provision phone number'));
        } finally {
            setProvisioning(false);
        }
    };

    const handleConnectNew = async () => {
        if (!newUsername.trim()) return;
        setConnecting(true);
        try {
            if (twoFASession) {
                await completeTwoFASession(twoFASession.session_id);
            }
            await createSocialAccount({
                platform: platform.key,
                username: newUsername,
                display_name: newDisplayName || newUsername,
                session_id: twoFASession?.session_id,
                project_id: projectId,
            });
            onConnected();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to connect account'));
        } finally {
            setConnecting(false);
        }
    };

    return (
        <div className="wizard-platform-card">
            <div className="wizard-platform-title">
                <div
                    className={`platform-icon-circle ${platformIconClass(platform.key)}`}
                    style={{ width: 32, height: 32, fontSize: 14 }}
                >
                    <PlatformIcon platform={platform.key} size={16} />
                </div>
                {platform.label}
            </div>

            {/* Account mode toggle */}
            <div className="account-mode-toggle">
                {isOAuthAvailable && (
                    <button
                        className={`account-mode-btn ${accountMode === 'oauth' ? 'active' : ''}`}
                        onClick={() => setAccountMode('oauth')}
                        style={accountMode === 'oauth' ? {
                            background: platform.key === 'facebook' ? '#1877f2' : platform.key === 'instagram' ? 'linear-gradient(45deg, #f09433, #dc2743, #bc1888)' : platform.key === 'twitter' ? '#000' : '#0077b5',
                            color: '#fff',
                            borderColor: platform.key === 'facebook' ? '#1877f2' : platform.key === 'instagram' ? '#dc2743' : platform.key === 'twitter' ? '#000' : '#0077b5',
                        } : {}}
                    >
                        {platform.key === 'facebook' ? <Facebook size={14} /> : platform.key === 'instagram' ? <Instagram size={14} /> : platform.key === 'twitter' ? <Twitter size={14} /> : <Linkedin size={14} />}
                        Connect with {platform.label}
                    </button>
                )}
                <button
                    className={`account-mode-btn ${accountMode === 'new' ? 'active' : ''}`}
                    onClick={() => setAccountMode('new')}
                >
                    <Users size={14} />
                    I need to create an account
                </button>
            </div>

            {/* ── OAuth connect flow (Facebook) ── */}
            {accountMode === 'oauth' && platform.key === 'facebook' && !oauthCredentialId && (
                <div className="connect-form">
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                        Click below to connect your Facebook account. A popup will open for you to log in and authorize AIME to manage your Pages.
                    </p>
                    <GlassButton
                        variant="primary"
                        onClick={() => handleOAuthPopup('facebook')}
                        style={{ background: '#1877f2', borderColor: '#1877f2' }}
                        icon={<Facebook size={16} />}
                    >
                        {isFlbEnabled() ? 'Connect your Pages and Instagram' : 'Log in with Facebook'}
                    </GlassButton>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                        Connects your Facebook Pages. If your Instagram is linked to a Page, it'll be connected too.
                    </div>
                </div>
            )}

            {accountMode === 'oauth' && platform.key === 'facebook' && oauthCredentialId && (
                <div className="connect-form">
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                        Select Pages to connect
                    </div>
                    {loadingPages ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <Loader size={14} className="spin" /> Loading your Facebook Pages...
                        </div>
                    ) : facebookPages.length === 0 ? (
                        <div style={{ padding: 16, color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>
                            {isFlbEnabled() ? (
                                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                    <strong style={{ color: 'var(--text)' }}>Facebook returned no Pages.</strong>
                                    <br /><br />
                                    This usually means:
                                    <ul style={{ textAlign: 'left', margin: '8px 0', paddingLeft: '20px' }}>
                                        <li>You skipped the page selection step in Meta's consent dialog</li>
                                        <li>The Page is owned by a Business and you didn't pick that Business in the consent flow</li>
                                        <li>You don't admin any Pages</li>
                                    </ul>
                                    <strong>To fix:</strong>
                                    <ol style={{ textAlign: 'left', margin: '8px 0', paddingLeft: '20px' }}>
                                        <li>If you recently removed the AIME app in Facebook's settings, wait a few minutes — Meta re-links your Pages lazily — then click "Reload Pages" below</li>
                                        <li>Still empty? Click "Reset Facebook connection", then "Connect your Pages and Instagram" again — Meta will show the full consent dialog. Pick the Business (or "Pages you manage personally") that owns the Page and tick each Page</li>
                                        <li>Only as a last resort remove the AIME app from your Facebook account settings first — removal revokes ALL existing Page grants (including other AIME connections using this Facebook account) and Meta can take several minutes to accept new ones afterwards</li>
                                    </ol>
                                </div>
                            ) : (
                                <>
                                    <div style={{ marginBottom: 10 }}>
                                        <Info size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                        Facebook returned no Pages.
                                    </div>
                                    <div style={{ marginBottom: 12 }}>
                                        This usually means the page selection step was skipped during consent, or your Page grants were invalidated (e.g. after removing the AIME app in Facebook settings, or linking Instagram to a Page in Meta Business Suite). To fix:
                                        <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 22 }}>
                                            <li>If you recently removed the AIME app on Facebook, wait a few minutes (Meta re-links Pages lazily), then click <b>Reload Pages</b> below</li>
                                            <li>Still empty? Click <b>Reset Facebook connection</b>, then <b>Log in with Facebook</b> again — Meta will show the full consent dialog. Tick each Page you want to connect.</li>
                                            <li>Only as a last resort remove the AIME app from <a href="https://www.facebook.com/settings?tab=applications" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)' }}>your Facebook account settings</a> — removal revokes ALL existing Page grants for this Facebook account and Meta can take several minutes to accept new ones afterwards</li>
                                        </ol>
                                    </div>
                                </>
                            )}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <GlassButton variant="primary" onClick={reloadFacebookPages}>
                                    Reload Pages
                                </GlassButton>
                                <GlassButton variant="secondary" onClick={handleResetFacebookCredential}>
                                    Reset Facebook connection
                                </GlassButton>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="facebook-page-list">
                                {facebookPages.map(page => (
                                    <label key={page.page_id} className={`facebook-page-row ${selectedPageIds.has(page.page_id) ? 'selected' : ''} ${page.already_connected ? 'disabled' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={selectedPageIds.has(page.page_id) || page.already_connected}
                                            onChange={() => togglePageSelection(page.page_id)}
                                            disabled={page.already_connected}
                                        />
                                        {page.picture_url && <img src={page.picture_url} className="page-avatar" alt="" />}
                                        <div style={{ flex: 1 }}>
                                            <div className="page-name">{page.name}</div>
                                            {page.category && <div className="page-category">{page.category}</div>}
                                            {page.instagram && (
                                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                                                    Instagram @{page.instagram.username}
                                                    {page.instagram_already_connected ? ' — already connected' : ' — not connected'}
                                                </div>
                                            )}
                                        </div>
                                        {page.already_connected && <GlassBadge variant="success">Connected</GlassBadge>}
                                    </label>
                                ))}
                            </div>
                            {facebookPages.some(p => p.already_connected) && (
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                                    Pages marked Connected are already set up and aren't affected. When Facebook asks which Pages to share, keep your existing Pages ticked, since unticking one there can stop AIME from publishing to it.
                                </div>
                            )}
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                                Ticking a Page also connects its linked Instagram account, if it has one.
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                                <GlassButton
                                    variant="primary"
                                    onClick={handleConnectSelectedPages}
                                    disabled={selectedPageIds.size === 0 || connectingPages}
                                >
                                    {connectingPages ? 'Connecting...' : `Connect ${selectedPageIds.size} Page${selectedPageIds.size !== 1 ? 's' : ''}`}
                                </GlassButton>
                                <GlassButton variant="secondary" onClick={handleCancelPageSelection}>
                                    Cancel
                                </GlassButton>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── OAuth connect flow (Instagram — direct login) ── */}
            {accountMode === 'oauth' && platform.key === 'instagram' && !oauthCredentialId && (
                <div className="connect-form">
                    <GlassButton
                        variant="primary"
                        onClick={handleInstagramConnect}
                        style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', borderColor: 'transparent' }}
                        icon={<Instagram size={16} />}
                    >
                        Connect Instagram
                    </GlassButton>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 0' }}>
                        Connects your Instagram Business or Creator account directly. No Facebook required.
                    </p>
                </div>
            )}

            {accountMode === 'oauth' && platform.key === 'instagram' && oauthCredentialId && (
                <div className="connect-form">
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                        Select Instagram accounts to connect
                    </div>
                    {loadingInstagram ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <Loader size={14} className="spin" /> Loading your Instagram accounts...
                        </div>
                    ) : instagramAccounts.length === 0 ? (
                        <div style={{ padding: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
                                <Info size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                                <span>No Instagram Business accounts found. To connect Instagram:</span>
                            </div>
                            <ol style={{ margin: '0 0 12px', paddingLeft: 24, lineHeight: 1.8 }}>
                                <li>Make sure your Instagram account is set to <strong>Business</strong> or <strong>Creator</strong> (not Personal) in the Instagram app under <em>Settings → Account type and tools</em>.</li>
                                <li>Link it to your Facebook Page via <em>Facebook Page → Settings → Linked accounts → Instagram</em>, or in <a href="https://business.facebook.com/settings/instagram" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)' }}>Meta Business Suite</a>.</li>
                                <li>Come back here and click <strong>Find Instagram Accounts</strong> again.</li>
                            </ol>
                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setOauthCredentialId(null)}
                            >
                                Try Again
                            </GlassButton>
                        </div>
                    ) : (
                        <>
                            <div className="facebook-page-list">
                                {instagramAccounts.map(acct => (
                                    <label key={acct.instagram_id} className={`facebook-page-row ${selectedInstagramIds.has(acct.instagram_id) ? 'selected' : ''} ${acct.already_connected ? 'disabled' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={selectedInstagramIds.has(acct.instagram_id) || acct.already_connected}
                                            onChange={() => toggleInstagramSelection(acct.instagram_id)}
                                            disabled={acct.already_connected}
                                        />
                                        {acct.profile_picture_url && <img src={acct.profile_picture_url} className="page-avatar" alt="" />}
                                        <div style={{ flex: 1 }}>
                                            <div className="page-name">@{acct.username}</div>
                                            <div className="page-category">{acct.name}{acct.followers_count > 0 ? ` · ${acct.followers_count.toLocaleString()} followers` : ''}</div>
                                        </div>
                                        {acct.already_connected && <GlassBadge variant="success">Connected</GlassBadge>}
                                    </label>
                                ))}
                            </div>
                            <GlassButton
                                variant="primary"
                                onClick={handleConnectInstagramAccounts}
                                disabled={selectedInstagramIds.size === 0 || connectingInstagram}
                                style={{ marginTop: 12 }}
                            >
                                {connectingInstagram ? 'Connecting...' : `Connect ${selectedInstagramIds.size} Account${selectedInstagramIds.size !== 1 ? 's' : ''}`}
                            </GlassButton>
                        </>
                    )}
                </div>
            )}

            {/* ── OAuth connect flow (LinkedIn) ── */}
            {accountMode === 'oauth' && platform.key === 'linkedin' && !linkedinPages.length && (
                <div className="connect-form">
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                        {linkedinCompanyPagesEnabled
                            ? 'Connect your LinkedIn profile or Company Pages. A popup will open to authorise AIME.'
                            : 'Click below to connect your LinkedIn profile. A popup will open for you to log in and authorize AIME.'}
                    </p>
                    <GlassButton
                        variant="primary"
                        onClick={() => handleOAuthPopup('linkedin')}
                        disabled={linkedinConnecting || loadingLinkedinPages}
                        style={{ background: '#0077b5', borderColor: '#0077b5' }}
                        icon={<Linkedin size={16} />}
                    >
                        {(linkedinConnecting || loadingLinkedinPages) ? 'Connecting...' : 'Log in with LinkedIn'}
                    </GlassButton>
                    {(linkedinConnecting || loadingLinkedinPages) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <Loader size={14} className="spin" />
                            {loadingLinkedinPages ? 'Fetching your Company Pages...' : 'Fetching your LinkedIn profile...'}
                        </div>
                    )}
                    {!linkedinCompanyPagesEnabled && (
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, padding: '8px 12px', background: 'var(--panel-strong)', borderRadius: 6, border: '1px solid var(--stroke)' }}>
                            Currently connects your personal LinkedIn profile. Company Page support coming soon, pending LinkedIn approval.
                        </p>
                    )}
                </div>
            )}

            {/* ── LinkedIn Company Page picker (flag-gated) ── */}
            {accountMode === 'oauth' && platform.key === 'linkedin' && linkedinPages.length > 0 && (
                <div className="connect-form">
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                        Select LinkedIn Company Pages to connect:
                    </p>
                    {loadingLinkedinPages ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <Loader size={14} className="spin" /> Loading Company Pages...
                        </div>
                    ) : (
                        <>
                            <div className="facebook-pages-list">
                                {linkedinPages.map(page => (
                                    <label key={page.id} className={`facebook-page-row ${selectedLinkedinPageIds.has(page.id) ? 'selected' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={selectedLinkedinPageIds.has(page.id)}
                                            onChange={() => setSelectedLinkedinPageIds(prev => {
                                                const next = new Set(prev);
                                                next.has(page.id) ? next.delete(page.id) : next.add(page.id);
                                                return next;
                                            })}
                                        />
                                        <Linkedin size={20} style={{ color: '#0077b5', flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                            <div className="page-name">{page.name}</div>
                                            {page.vanity_name && <div className="page-category">linkedin.com/company/{page.vanity_name}</div>}
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <GlassButton
                                variant="primary"
                                onClick={handleConnectLinkedinPages}
                                disabled={selectedLinkedinPageIds.size === 0 || connectingLinkedinPages}
                                style={{ marginTop: 12 }}
                            >
                                {connectingLinkedinPages ? 'Connecting...' : `Connect ${selectedLinkedinPageIds.size} Page${selectedLinkedinPageIds.size !== 1 ? 's' : ''}`}
                            </GlassButton>
                        </>
                    )}
                </div>
            )}

            {/* ── OAuth connect flow (Twitter/X) ── */}
            {accountMode === 'oauth' && platform.key === 'twitter' && (
                <div className="connect-form">
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                        Click below to connect your X (Twitter) account. A popup will open for you to log in and authorize AIME.
                    </p>
                    <GlassButton
                        variant="primary"
                        onClick={() => handleOAuthPopup('twitter')}
                        disabled={twitterConnecting}
                        style={{ background: '#000', borderColor: '#000' }}
                        icon={<Twitter size={16} />}
                    >
                        {twitterConnecting ? 'Connecting...' : 'Log in with X'}
                    </GlassButton>
                    {twitterConnecting && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <Loader size={14} className="spin" /> Fetching your X profile...
                        </div>
                    )}
                </div>
            )}

            {/* ── New account flow ── */}
            {accountMode === 'new' && (
                <div className="connect-form">
                    {/* Phone number choice */}
                    <div className="phone-choice-section">
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                            Phone number for verification
                        </div>
                        <div className="radio-option-row">
                            <label className={`radio-option-label ${phoneChoice === 'aime' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name={`phone-choice-${platform.key}`}
                                    checked={phoneChoice === 'aime'}
                                    onChange={() => setPhoneChoice('aime')}
                                />
                                <Phone size={14} />
                                AiMe provides a number
                            </label>
                            <label className={`radio-option-label ${phoneChoice === 'own' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name={`phone-choice-${platform.key}`}
                                    checked={phoneChoice === 'own'}
                                    onChange={() => setPhoneChoice('own')}
                                />
                                <Phone size={14} />
                                Use my own number
                            </label>
                        </div>

                        {phoneChoice === 'aime' && !twoFASession && (
                            <GlassSelect
                                label="Country"
                                options={COUNTRY_OPTIONS}
                                value={countryCode}
                                onChange={setCountryCode}
                            />
                        )}

                        {phoneChoice === 'own' && !twoFASession && (
                            <GlassInput
                                label="Your Phone Number"
                                placeholder="+61 4xx xxx xxx"
                                value={ownNumber}
                                onChange={setOwnNumber}
                            />
                        )}

                        {!twoFASession && (
                            <GlassButton
                                variant="secondary"
                                onClick={handleProvisionNumber}
                                loading={provisioning}
                                disabled={phoneChoice === 'own' && !ownNumber.trim()}
                            >
                                {phoneChoice === 'aime' ? 'Provision Number' : 'Use This Number'}
                            </GlassButton>
                        )}

                        {twoFASession && (
                            <>
                                <div className="provisioned-number-box">
                                    <Phone size={16} />
                                    {twoFASession.phone_number}
                                    {twoFASession.stub_mode && (
                                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                                            (stub mode)
                                        </span>
                                    )}
                                </div>

                                {twoFASession.status === 'received' && twoFASession.code ? (
                                    <div className="code-received-box">
                                        <CheckCircle size={18} color="#22c55e" />
                                        <div>
                                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>
                                                Code received
                                            </div>
                                            <div className="code-received-value">{twoFASession.code}</div>
                                        </div>
                                    </div>
                                ) : phoneChoice === 'aime' ? (
                                    <div className="code-waiting-box">
                                        <div className="polling-spinner" />
                                        Waiting for verification code... (checks every 3 seconds)
                                    </div>
                                ) : (
                                    <div className="code-waiting-box">
                                        <Info size={14} />
                                        Enter the code sent to your number on the {platform.label} registration form.
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Setup steps */}
                    {twoFASession && (
                        <div className="setup-steps-list">
                            {PLATFORM_SETUP_STEPS[platform.key].map((step, i) => (
                                <div key={i} className="setup-step">
                                    <span className="setup-step-num">{i + 1}</span>
                                    {step}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Connect form */}
                    {twoFASession && (
                        <>
                            <div className="connect-form-row">
                                <GlassInput
                                    label="Username"
                                    placeholder="Choose a username"
                                    value={newUsername}
                                    onChange={setNewUsername}
                                />
                                <GlassInput
                                    label="Display Name"
                                    placeholder="Your Name or Page Name"
                                    value={newDisplayName}
                                    onChange={setNewDisplayName}
                                />
                            </div>

                            <label className="completion-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={setupComplete}
                                    onChange={(e) => setSetupComplete(e.target.checked)}
                                />
                                I have completed account creation on {platform.label}
                            </label>

                            <GlassButton
                                variant="primary"
                                onClick={handleConnectNew}
                                loading={connecting}
                                disabled={!setupComplete || !newUsername.trim()}
                            >
                                Connect {platform.label} Account
                            </GlassButton>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main Module
// ---------------------------------------------------------------------------

const SocialAccountsModule: React.FC = () => {
    const ctx = useOutletContext<{ selectedProjectId?: string }>();
    const selectedProjectId = ctx?.selectedProjectId;
    const [activeTab, setActiveTab] = useState('connected');
    const [accounts, setAccounts] = useState<SocialAccountData[]>([]);
    const [loading, setLoading] = useState(true);
    const [facebookOAuthConfigured, setFacebookOAuthConfigured] = useState(false);
    const [linkedinOAuthConfigured, setLinkedinOAuthConfigured] = useState(false);
    const [linkedinCompanyPagesEnabled, setLinkedinCompanyPagesEnabled] = useState(false);
    const [instagramOAuthConfigured, setInstagramOAuthConfigured] = useState(false);
    const [twitterOAuthConfigured, setTwitterOAuthConfigured] = useState(false);
    const [applyingAll, setApplyingAll] = useState(false);
    const [applyAllResults, setApplyAllResults] = useState<ApplyBrandingAllResult[] | null>(null);
    const [previewsOpen, setPreviewsOpen] = useState<Record<string, boolean>>({});
    const [previews, setPreviews] = useState<Record<string, BrandingPreview>>({});
    const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({});
    const [applyingBranding, setApplyingBranding] = useState<Record<string, boolean>>({});
    // Facebook Page Branding Live Push (Phase 1B) — draft review modal state.
    const [brandingModalAccount, setBrandingModalAccount] = useState<SocialAccountData | null>(null);
    const [brandingDraftData, setBrandingDraftData] = useState<BrandingDraftResponse | null>(null);
    // Auto-connected-Instagram toast (see BLOCKER-1, flb-instagram-autocreate-
    // scope-fix code review iteration 1). Owned by the parent module — not by
    // WizardPlatformCard — specifically so it survives WizardPlatformCard
    // unmounting when handleConnectSelectedPages's onConnected() switches
    // activeTab to 'connected'. Rendered unconditionally at the bottom of this
    // component's JSX so it is visible regardless of which tab is active.
    const [igAutoConnectedToast, setIgAutoConnectedToast] = useState<string>('');
    const igAutoConnectedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleIgAutoConnected = useCallback((usernames: string) => {
        setIgAutoConnectedToast(usernames);
        if (igAutoConnectedTimeoutRef.current) clearTimeout(igAutoConnectedTimeoutRef.current);
        igAutoConnectedTimeoutRef.current = setTimeout(() => setIgAutoConnectedToast(''), 6000);
    }, []);
    const [selectedPlatforms, setSelectedPlatforms] = useState<Record<PlatformKey, boolean>>({
        instagram: true,
        facebook: true,
        linkedin: true,
        twitter: true,
    });

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getSocialAccounts(selectedProjectId);
            setAccounts(data);
        } catch (err) {
            console.error('Failed to fetch social accounts:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedProjectId]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    useEffect(() => {
        checkFacebookOAuthConfig()
            .then(data => setFacebookOAuthConfigured(data.configured))
            .catch(() => setFacebookOAuthConfigured(false));
        checkLinkedInOAuthConfig()
            .then(data => {
                setLinkedinOAuthConfigured(data.configured);
                setLinkedinCompanyPagesEnabled(data.company_pages_enabled ?? false);
            })
            .catch(() => setLinkedinOAuthConfigured(false));
        checkInstagramOAuthConfig()
            .then(data => setInstagramOAuthConfigured(data.configured))
            .catch(() => setInstagramOAuthConfigured(false));
        checkTwitterOAuthConfig()
            .then(data => setTwitterOAuthConfigured(data.configured))
            .catch(() => setTwitterOAuthConfigured(false));
    }, []);

    const handleDisconnect = async (account: SocialAccountData) => {
        const plat = PLATFORMS.find((p) => p.key === account.platform);
        const label = accountIdentityLabel(account);
        if (!window.confirm(`Disconnect ${label} (${plat?.label ?? account.platform})?`)) return;
        try {
            await deleteSocialAccount(account.account_id);
            fetchAccounts();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to disconnect account'));
        }
    };

    const handleApplyBranding = async (accountId: string) => {
        const account = accounts.find((a) => a.account_id === accountId);
        setApplyingBranding((prev) => ({ ...prev, [accountId]: true }));
        try {
            if (account?.platform === 'facebook') {
                // Facebook Page Branding Live Push (Phase 1B): no longer a
                // fire-and-forget call — it spends image quota, builds an
                // inspectable draft, and opens the review modal rather than
                // silently flipping branding_applied.
                const result = await createBrandingDraft(accountId);
                setBrandingDraftData(result);
                setBrandingModalAccount(account);
                fetchAccounts();
            } else {
                await applySocialBranding(accountId);
                fetchAccounts();
            }
        } catch (err: any) {
            alert(getApiError(err, 'Failed to apply branding'));
        } finally {
            setApplyingBranding((prev) => ({ ...prev, [accountId]: false }));
        }
    };

    const handleReopenBrandingDraft = async (accountId: string) => {
        const account = accounts.find((a) => a.account_id === accountId);
        if (!account) return;
        setApplyingBranding((prev) => ({ ...prev, [accountId]: true }));
        try {
            const existing = await getBrandingDraft(accountId);
            if (existing.draft) {
                // GET /branding-draft now returns the real capability/
                // brand_assets/project_contact/page_current alongside the
                // draft (see B2, 2026-09-17 code review) — no more
                // fabricating a false can_push:false / all-null stub.
                setBrandingDraftData({
                    draft: existing.draft,
                    capability: existing.capability ?? { can_push: false, reason: null, missing_scopes: [], granted_scopes: [] },
                    quota_reason: existing.quota_reason ?? null,
                    brand_assets: existing.brand_assets ?? {
                        logo_url: null, favicon_url: null, logo_mark_url: null,
                        logo_dark_bg_url: null, logo_monochrome_white_url: null, logo_monochrome_black_url: null,
                        brand_guide_id: null, can_edit_brand_guide: false,
                    },
                    project_contact: existing.project_contact ?? null,
                    page_current: existing.page_current ?? null,
                });
                setBrandingModalAccount(account);
            } else {
                await handleApplyBranding(accountId);
            }
        } catch (err: any) {
            alert(getApiError(err, 'Failed to open branding draft'));
        } finally {
            setApplyingBranding((prev) => ({ ...prev, [accountId]: false }));
        }
    };

    const handleApplyAll = async () => {
        setApplyingAll(true);
        setApplyAllResults(null);
        try {
            // The backend returns a rich per-account list (applied/reason/
            // error/field_results) — previously discarded into a generic
            // fetchAccounts() with no visibility into partial failures
            // (I8, 2026-09-17 code review). Surface it in a results panel
            // instead of a silent refresh.
            const results = await applyAllSocialBranding(selectedProjectId);
            setApplyAllResults(Array.isArray(results) ? results : null);
            fetchAccounts();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to apply branding to all'));
        } finally {
            setApplyingAll(false);
        }
    };

    const handleTogglePreview = async (accountId: string) => {
        if (previewsOpen[accountId]) {
            setPreviewsOpen((prev) => ({ ...prev, [accountId]: false }));
            return;
        }
        setPreviewsOpen((prev) => ({ ...prev, [accountId]: true }));
        if (!previews[accountId]) {
            setPreviewLoading((prev) => ({ ...prev, [accountId]: true }));
            try {
                const data = await getSocialBrandingPreview(accountId);
                setPreviews((prev) => ({ ...prev, [accountId]: data }));
            } catch {
                // ignore
            } finally {
                setPreviewLoading((prev) => ({ ...prev, [accountId]: false }));
            }
        }
    };

    const tabs = [
        { id: 'connected', label: 'Connected Accounts' },
        { id: 'wizard', label: 'Setup Wizard' },
        { id: 'branding', label: 'Branding' },
    ];

    const connectedAccounts = accounts.filter((a) => a.status === 'active');

    // Which platforms are already connected?
    const connectedPlatforms = new Set(connectedAccounts.map((a) => a.platform));

    // Groups active accounts by platform so the Connected tab can render one
    // card per account instead of collapsing multiple accounts on the same
    // platform (e.g. several Facebook Pages) into a single card via `.find()`.
    // See specs/completed/social-accounts-multi-page-fix.md Phase 1.
    const accountsByPlatform = useMemo(
        () => groupAccountsByPlatform(accounts, PLATFORMS.map((p) => p.key)),
        [accounts]
    );

    const addAnotherLabel = (platform: PlatformKey): string => {
        switch (platform) {
            case 'facebook': return 'Add another Facebook Page';
            case 'instagram': return 'Add another Instagram account';
            case 'linkedin': return 'Add another LinkedIn account';
            case 'twitter': return 'Add another X account';
        }
    };

    const startConnectFlow = (platformKey: PlatformKey) => {
        setSelectedPlatforms((prev) => ({
            instagram: false,
            facebook: false,
            linkedin: false,
            twitter: false,
            [platformKey]: true,
        }));
        setActiveTab('wizard');
    };

    const togglePlatform = (p: PlatformKey) => {
        setSelectedPlatforms((prev) => ({ ...prev, [p]: !prev[p] }));
    };

    // Shared between the Connected Accounts tab and the Branding tab — both
    // have their own "Apply Branding to All" bar and both should surface the
    // same rich per-account results instead of a silent alert() (I8).
    const renderApplyAllResultsPanel = () => {
        if (!applyAllResults) return null;
        return (
            <div className="apply-all-results-panel">
                <div className="apply-all-results-header">
                    <span>
                        Apply Branding to All — results ({applyAllResults.filter((r) => r.applied).length}/
                        {applyAllResults.length} applied)
                    </span>
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<X size={14} />}
                        onClick={() => setApplyAllResults(null)}
                    >
                        Dismiss
                    </GlassButton>
                </div>
                <div className="apply-all-results-list">
                    {applyAllResults.map((r) => {
                        const account = accounts.find((a) => a.account_id === r.account_id);
                        const label = account?.username || r.account_id;
                        const detail = r.applied
                            ? 'Applied'
                            : r.reason === 'reconnect_required'
                                ? 'Reconnect required — missing Facebook permission'
                                : r.reason === 'rate_limited'
                                    ? 'Skipped — Facebook rate limit reached during this run'
                                    : r.error
                                        ? (typeof r.error === 'string' ? r.error : JSON.stringify(r.error))
                                        : r.status
                                            ? `Not applied (${r.status})`
                                            : 'Not applied';
                        return (
                            <div key={r.account_id} className="apply-all-result-row">
                                {r.applied ? (
                                    <CheckCircle size={14} className="apply-all-result-icon-ok" />
                                ) : (
                                    <AlertTriangle size={14} className="apply-all-result-icon-warn" />
                                )}
                                <span className="apply-all-result-platform">{r.platform}</span>
                                <span className="apply-all-result-label">{label}</span>
                                <span className="apply-all-result-detail">{detail}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ── Connected Accounts Tab ──────────────────────────────────────────────

    const renderConnectedTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {connectedAccounts.length > 0 && (
                <div className="apply-all-bar">
                    <span className="apply-all-bar-text">
                        {connectedAccounts.length} account{connectedAccounts.length !== 1 ? 's' : ''} connected
                    </span>
                    <GlassButton
                        variant="primary"
                        size="sm"
                        icon={<Paintbrush size={14} />}
                        onClick={handleApplyAll}
                        loading={applyingAll}
                    >
                        Apply Branding to All
                    </GlassButton>
                </div>
            )}

            {renderApplyAllResultsPanel()}

            <div className="platform-grid">
                {PLATFORMS.map((plat) => {
                    const platAccounts = accountsByPlatform[plat.key];
                    if (platAccounts.length === 0) {
                        return (
                            <div key={plat.key} className="platform-card">
                                <div className="platform-card-header">
                                    <div className={`platform-icon-circle ${platformIconClass(plat.key)}`}>
                                        <PlatformIcon platform={plat.key} size={18} />
                                    </div>
                                    <div className="platform-card-info">
                                        <div className="platform-name">{plat.label}</div>
                                    </div>
                                </div>
                                <div className="platform-card-meta">
                                    <GlassBadge variant="default">Not Connected</GlassBadge>
                                </div>
                                <div className="platform-card-actions">
                                    <GlassButton
                                        variant="primary"
                                        size="sm"
                                        onClick={() => startConnectFlow(plat.key)}
                                    >
                                        Connect
                                    </GlassButton>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <React.Fragment key={plat.key}>
                            {platAccounts.map((account) => (
                                <div key={account.account_id} className="platform-card" data-account-id={account.account_id}>
                                    <div className="platform-card-header">
                                        <div className={`platform-icon-circle ${platformIconClass(plat.key)}`}>
                                            <PlatformIcon platform={plat.key} size={18} />
                                        </div>
                                        <div className="platform-card-info">
                                            <div className="platform-name">{plat.label}</div>
                                            <div className="platform-username" title={account.page_id ?? ''}>
                                                {accountIdentityLabel(account)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="platform-card-meta">
                                        <GlassBadge variant="success">Connected</GlassBadge>
                                        {(() => {
                                            const badge = brandingStatusBadge(account.branding_status, false);
                                            return badge ? <GlassBadge variant={badge.variant}>{badge.label}</GlassBadge> : null;
                                        })()}
                                        {account.ramp_days_remaining !== null && (
                                            <span className="ramp-indicator">
                                                Ramp: {account.ramp_days_remaining}d left
                                            </span>
                                        )}
                                    </div>

                                    <div className="platform-card-actions">
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            icon={<Paintbrush size={13} />}
                                            onClick={() => handleApplyBranding(account.account_id)}
                                            loading={applyingBranding[account.account_id]}
                                        >
                                            Re-apply
                                        </GlassButton>
                                        <GlassButton
                                            variant="danger"
                                            size="sm"
                                            icon={<Trash2 size={13} />}
                                            onClick={() => handleDisconnect(account)}
                                        >
                                            Disconnect
                                        </GlassButton>
                                    </div>
                                </div>
                            ))}
                            {SHOW_ADD_ANOTHER_ACCOUNT_CARD && (
                                <div key={`${plat.key}-add`} className="platform-card platform-card-add">
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        icon={<Plus size={13} />}
                                        onClick={() => startConnectFlow(plat.key)}
                                    >
                                        {addAnotherLabel(plat.key)}
                                    </GlassButton>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {connectedAccounts.length === 0 && !loading && (
                <div className="social-empty-state">
                    <Users size={48} className="empty-icon" />
                    <p>No social accounts connected yet. Use the Setup Wizard to connect your accounts.</p>
                    <GlassButton variant="primary" onClick={() => setActiveTab('wizard')}>
                        Open Setup Wizard
                    </GlassButton>
                </div>
            )}
        </div>
    );

    // ── Setup Wizard Tab ─────────────────────────────────────────────────────

    const renderWizardTab = () => {
        const activePlatforms = PLATFORMS.filter((p) => selectedPlatforms[p.key]);
        return (
            <div className="setup-wizard">
                {/* Platform selector */}
                <GlassCard padding="md">
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                        Select platforms to set up
                    </div>
                    <div className="platform-selector-row">
                        {PLATFORMS.map((plat) => (
                            <label
                                key={plat.key}
                                className={`platform-checkbox-label ${selectedPlatforms[plat.key] ? 'checked' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedPlatforms[plat.key]}
                                    onChange={() => togglePlatform(plat.key)}
                                />
                                <PlatformIcon platform={plat.key} size={14} />
                                {plat.label}
                                {connectedPlatforms.has(plat.key) && (
                                    <GlassBadge variant="success">Connected</GlassBadge>
                                )}
                            </label>
                        ))}
                    </div>
                </GlassCard>

                {/* Compliance disclosure */}
                <div className="compliance-disclosure">
                    <Info size={16} className="compliance-icon" />
                    <p>
                        AiMe's social account setup wizard assists you in creating your own accounts on
                        third-party platforms. All accounts are registered in your name and you remain the
                        account owner. AiMe does not automate account creation in violation of any
                        platform's Terms of Service.
                    </p>
                </div>

                {/* Per-platform wizard cards */}
                {activePlatforms.length === 0 && (
                    <div className="social-empty-state">
                        <Info size={32} className="empty-icon" />
                        <p>Select at least one platform above to continue.</p>
                    </div>
                )}

                {activePlatforms.map((plat) => (
                    <WizardPlatformCard
                        key={plat.key}
                        platform={plat}
                        projectId={selectedProjectId}
                        oauthConfigured={facebookOAuthConfigured}
                        linkedinOAuthConfigured={linkedinOAuthConfigured}
                        linkedinCompanyPagesEnabled={linkedinCompanyPagesEnabled}
                        instagramOAuthConfigured={instagramOAuthConfigured}
                        twitterOAuthConfigured={twitterOAuthConfigured}
                        existingAccounts={accounts}
                        onIgAutoConnected={handleIgAutoConnected}
                        onConnected={() => {
                            fetchAccounts();
                            setActiveTab('connected');
                        }}
                    />
                ))}
            </div>
        );
    };

    // ── Branding Tab ─────────────────────────────────────────────────────────

    const renderBrandingTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {connectedAccounts.length > 0 && (
                <div className="apply-all-bar">
                    <span className="apply-all-bar-text">
                        Apply your brand guide to all connected accounts at once
                    </span>
                    <GlassButton
                        variant="primary"
                        size="sm"
                        icon={<Paintbrush size={14} />}
                        onClick={handleApplyAll}
                        loading={applyingAll}
                    >
                        Apply to All
                    </GlassButton>
                </div>
            )}

            {renderApplyAllResultsPanel()}

            {connectedAccounts.length === 0 && !loading && (
                <div className="social-empty-state">
                    <Paintbrush size={48} className="empty-icon" />
                    <p>No connected accounts. Connect accounts in the Setup Wizard to apply branding.</p>
                </div>
            )}

            <div className="branding-accounts-list">
                {connectedAccounts.map((account) => (
                    <div key={account.account_id} className="branding-account-row">
                        <div className="branding-account-header">
                            <div className="branding-account-identity">
                                <div
                                    className={`platform-icon-circle ${platformIconClass(account.platform)}`}
                                    style={{ width: 36, height: 36, fontSize: 14 }}
                                >
                                    <PlatformIcon platform={account.platform} size={16} />
                                </div>
                                <div>
                                    <div className="branding-account-name">
                                        {account.display_name || account.username || account.account_id.slice(0, 8)}
                                    </div>
                                    <div className="branding-account-platform">
                                        {account.platform}
                                        {account.username && ` · @${account.username}`}
                                    </div>
                                </div>
                            </div>

                            <div className="branding-account-actions">
                                {(() => {
                                    const badge = brandingStatusBadge(account.branding_status, true);
                                    return badge ? <GlassBadge variant={badge.variant}>{badge.label}</GlassBadge> : null;
                                })()}
                                {account.branding_applied_at && (
                                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                        {new Date(account.branding_applied_at).toLocaleDateString()}
                                    </span>
                                )}
                                {account.branding_last_error && (
                                    <span className="branding-account-error">{account.branding_last_error}</span>
                                )}
                                {account.platform === 'facebook' ? (
                                    <GlassButton
                                        variant="primary"
                                        size="sm"
                                        icon={<Paintbrush size={13} />}
                                        onClick={() => handleReopenBrandingDraft(account.account_id)}
                                        loading={applyingBranding[account.account_id]}
                                    >
                                        Apply Branding
                                    </GlassButton>
                                ) : (
                                    <>
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            icon={previewsOpen[account.account_id] ? <ChevronUp size={13} /> : <Eye size={13} />}
                                            onClick={() => handleTogglePreview(account.account_id)}
                                            loading={previewLoading[account.account_id]}
                                        >
                                            {previewsOpen[account.account_id] ? 'Hide Preview' : 'Preview Branding'}
                                        </GlassButton>
                                        <GlassButton
                                            variant="primary"
                                            size="sm"
                                            icon={<Paintbrush size={13} />}
                                            onClick={() => handleApplyBranding(account.account_id)}
                                            loading={applyingBranding[account.account_id]}
                                        >
                                            Apply Branding
                                        </GlassButton>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Preview panel */}
                        {previewsOpen[account.account_id] && previews[account.account_id] && (
                            <div className="branding-preview-panel">
                                {previews[account.account_id].stub_mode && (
                                    <GlassBadge variant="warning">Preview only — stub mode (no OAuth)</GlassBadge>
                                )}
                                <div>
                                    <div className="branding-preview-section-title">Bio Text</div>
                                    <div className="branding-preview-text">
                                        {previews[account.account_id].bio_text}
                                    </div>
                                </div>
                                <div>
                                    <div className="branding-preview-section-title">Profile Image Prompt</div>
                                    <div className="branding-preview-prompt">
                                        {previews[account.account_id].profile_image_prompt}
                                    </div>
                                </div>
                                <div>
                                    <div className="branding-preview-section-title">Banner Image Prompt</div>
                                    <div className="branding-preview-prompt">
                                        {previews[account.account_id].banner_image_prompt}
                                    </div>
                                </div>
                            </div>
                        )}
                        {previewsOpen[account.account_id] && previewLoading[account.account_id] && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
                                <Loader size={14} />
                                Generating branding preview...
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    if (!selectedProjectId) {
        return (
            <div className="social-accounts-module">
                <ModuleHeader title="Social Accounts" breadcrumb={['Marketing', 'Social Accounts']} />
                <GlassCard padding="lg">
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                        <Users size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                        <h3 style={{ margin: '0 0 8px', color: 'var(--text)' }}>No Project Selected</h3>
                        <p>Select a project from the sidebar to manage its social accounts.</p>
                    </div>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="social-accounts-module">
            <ModuleHeader
                title="Social Accounts"
                breadcrumb={['Marketing', 'Social Accounts']}
                actions={[
                    <GlassButton
                        key="setup"
                        variant="primary"
                        size="sm"
                        icon={<Users size={15} />}
                        onClick={() => setActiveTab('wizard')}
                    >
                        Setup Wizard
                    </GlassButton>,
                ]}
            />

            <GlassTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

            {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', padding: 24 }}>
                    <Loader size={16} />
                    Loading accounts...
                </div>
            ) : (
                <>
                    {activeTab === 'connected' && renderConnectedTab()}
                    {activeTab === 'wizard' && renderWizardTab()}
                    {activeTab === 'branding' && renderBrandingTab()}
                </>
            )}

            {brandingModalAccount && (
                <FacebookBrandingModal
                    isOpen={!!brandingModalAccount}
                    accountId={brandingModalAccount.account_id}
                    accountLabel={
                        brandingModalAccount.display_name ||
                        brandingModalAccount.username ||
                        brandingModalAccount.account_id.slice(0, 8)
                    }
                    draftData={brandingDraftData}
                    onClose={() => {
                        setBrandingModalAccount(null);
                        setBrandingDraftData(null);
                        fetchAccounts();
                    }}
                    onDraftUpdated={(data) => setBrandingDraftData(data)}
                    onReconnect={() => {
                        setBrandingModalAccount(null);
                        setBrandingDraftData(null);
                        // Switch to the wizard tab so WizardPlatformCard (and its
                        // postMessage listener that handles the OAuth popup's
                        // 'oauth_success' result — page selection, account refresh)
                        // is mounted by the time the popup completes, then
                        // immediately open the same FLB-aware Facebook OAuth popup
                        // the normal "Connect Facebook" button uses. Both calls stay
                        // synchronous within this click handler so window.open isn't
                        // blocked as a non-user-gesture popup.
                        setActiveTab('wizard');
                        openFacebookOAuthPopup(selectedProjectId);
                    }}
                />
            )}

            {/* Auto-connected-Instagram toast — rendered here, unconditionally,
                so it is visible regardless of activeTab (including right after
                onConnected() switches to 'connected'), not inside a
                WizardPlatformCard instance that unmounts on that switch. */}
            {igAutoConnectedToast && (
                <div className="social-ig-autoconnect-toast">
                    <CheckCircle size={14} color="var(--color-success)" style={{ flexShrink: 0 }} />
                    Also connected Instagram @{igAutoConnectedToast} (linked to the Facebook Page you selected).
                </div>
            )}
        </div>
    );
};

export default SocialAccountsModule;
